/**
 * /legal/data-retention — the LumoHR application's Data Retention & Deletion
 * Policy (public, unauthenticated). A STATIC content page: no state, no API, no
 * form. Reachable without a session so an employer evaluating LumoHR — or any
 * visitor — can read how their data is handled; linked from the login + register
 * pages (the app's public entry points).
 *
 * The policy body is published VERBATIM from the policy section (§1–§7) of the
 * source doc. Only §1–§7 is included here — no other part of that file is copied
 * or rendered. The wording is deliberate ("at least", "current understanding",
 * "we follow the law") — do NOT tidy it.
 *
 * Scope: requests go to hello@lumohr.com, handled manually within 30 days. This
 * page does NOT wire up account-deletion, export, or request handling (out of
 * scope; see ADR-0021).
 */
import { Link, createRoute } from '@tanstack/react-router';
import { rootRoute } from './root.js';

/** Publish date (the "last updated" stamp). */
const LAST_UPDATED = '4 July 2026';

export const legalDataRetentionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/data-retention',
  component: DataRetentionPolicyPage,
});

function DataRetentionPolicyPage(): JSX.Element {
  return (
    <main className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-2xl px-6 py-12">
        <header className="mb-10 space-y-2 border-b border-gray-800 pb-6">
          <Link to="/login" className="text-sm text-gray-500 underline hover:text-gray-300">
            ← LumoHR
          </Link>
          <h1 className="font-serif text-3xl">Data Retention &amp; Deletion Policy</h1>
          <p className="text-sm text-gray-400">
            Last updated: {LAST_UPDATED} · app.lumohr.com (the LumoHR application)
          </p>
        </header>

        <div className="space-y-8 text-sm leading-relaxed text-gray-300">
          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">1. What this policy covers</h2>
            <p>
              This policy explains{' '}
              <strong>
                how long LumoHR keeps personal data, what happens when you ask us to delete it, and
                what we are required by law to retain.
              </strong>{' '}
              It applies to the LumoHR application. (The marketing site has its own, simpler privacy
              policy.)
            </p>
            <p>
              We&apos;ve written this to be straight with you: some data we delete on request, and
              some data we are <strong>required by law to keep for a set period</strong> even if you
              ask us to delete it. This policy explains which is which.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">
              2. Two kinds of data — and why it affects deletion
            </h2>
            <ul className="list-disc space-y-3 pl-5">
              <li>
                <strong>Your organisation&apos;s own account data</strong> — your admin users,
                billing details, and company contacts. You are our customer, and we handle this on
                your instruction. We can delete it when you close your account (subject to the
                limited legal-retention rules in §4).
              </li>
              <li>
                <strong>Your employees&apos; data</strong> — payroll records, personal details,
                timesheets. Here{' '}
                <strong>
                  you (the employer) are the data controller and LumoHR acts as your processor
                </strong>{' '}
                — we handle it on your behalf. Much of this is subject to{' '}
                <strong>statutory retention</strong> (§4): the law requires it to be kept for a set
                period, so it cannot be deleted before then.
              </li>
            </ul>
            <p>
              This is why closing your account does not erase everything instantly — some records we
              are legally required to retain.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">3. When you close your account</h2>
            <p>When you ask to close your LumoHR account:</p>
            <p>
              <strong>We delete (after a 30-day grace period):</strong> your organisation&apos;s
              account and admin-user data, your billing and contact details, and any data not
              subject to a statutory retention requirement. We use a{' '}
              <strong>soft-delete → 30-day grace period → permanent deletion</strong> process, so an
              accidental closure can be reversed within the grace window.
            </p>
            <p>
              <strong>We retain (because the law requires it):</strong> payroll and tax records, for
              the statutory period for the country concerned (§4). These are minimised and archived
              — kept only to meet the legal requirement, not used for anything else — and deleted
              once the period ends.
            </p>
            <p>
              You can request an export of your data before deletion so you keep your own copy (see
              §6).
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">4. Statutory retention</h2>
            <p>
              Tax and payroll law in the countries we operate in <strong>requires</strong> certain
              payroll and tax records to be kept for a minimum period, regardless of a deletion
              request. This is a legal obligation that LumoHR meets on your behalf. Our current
              understanding of these minimum periods is:
            </p>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr className="border-b border-gray-700 text-xs uppercase tracking-wide text-gray-400">
                    <th className="py-2 pr-4 font-medium">Country</th>
                    <th className="py-2 pr-4 font-medium">
                      Minimum retention of payroll/tax records
                    </th>
                    <th className="py-2 font-medium">Counted from</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4 font-medium text-white">Nigeria</td>
                    <td className="py-2 pr-4">at least 6 years</td>
                    <td className="py-2">the end of the relevant tax year (31 December)</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4 font-medium text-white">Ghana</td>
                    <td className="py-2 pr-4">at least 6 years</td>
                    <td className="py-2">the end of the relevant tax year (31 December)</td>
                  </tr>
                  <tr className="border-b border-gray-800">
                    <td className="py-2 pr-4 font-medium text-white">DRC</td>
                    <td className="py-2 pr-4">at least 10 years</td>
                    <td className="py-2">the end of the relevant tax year (31 December)</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>
              The period runs <strong>per record, from the end of its tax year</strong> — so at
              account closure, your most recent records are each kept until their own period expires.
              If any records are subject to an ongoing audit, dispute, or legal claim, they are kept
              until that is resolved. We keep only what the law requires, in minimised form, and
              delete it when the period ends.
            </p>
            <p className="italic text-gray-400">
              (These periods reflect our current understanding of the applicable tax and records
              legislation and are kept under review; where the law requires a different period, we
              follow the law.)
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">5. Your employees&apos; requests</h2>
            <p>
              If one of <strong>your employees</strong> contacts us directly to access, correct, or
              delete their personal data, we will{' '}
              <strong>refer that request to you (the employer)</strong>, because you are the data
              controller and the decision is yours to make (within the statutory-retention limits in
              §4). We help you respond, but we do not action an employee&apos;s request over your
              records without your instruction.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">6. How to make a request</h2>
            <p>
              To close your account, request a data export, or ask about this policy, email{' '}
              <a href="mailto:hello@lumohr.com" className="underline hover:text-white">
                hello@lumohr.com
              </a>
              . Please note we handle these requests <strong>manually</strong>: we will{' '}
              <strong>acknowledge and action your request within 30 days</strong>. We will confirm
              what will be deleted and what must be retained under §4, and carry it out accordingly.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-serif text-xl text-white">7. Contact and changes</h2>
            <p>
              <a href="mailto:hello@lumohr.com" className="underline hover:text-white">
                hello@lumohr.com
              </a>{' '}
              — Mulanji Technologies Solution Limited (RC: 1909785), Nigeria. We may update this
              policy from time to time; the &quot;last updated&quot; date shows when, and we keep it
              under review.
            </p>
          </section>
        </div>

        <footer className="mt-12 border-t border-gray-800 pt-6 text-center">
          <Link to="/login" className="text-sm text-gray-400 underline hover:text-white">
            Back to sign in
          </Link>
        </footer>
      </div>
    </main>
  );
}
